import { useExpander } from '@app/utils/hooks/useExpander';
import { isDefined } from '@app/utils/is';
import { Checkboxes } from '@typings';
import cx from 'classnames';
import React from 'react';

import { Button } from '../Button';
import { Checkbox } from '../Fields/Checkbox';
import { TextEllipsis } from '../TextEllipsis';

import { CheckboxGroup } from './CheckboxGroup';

interface Props {
  options: Checkboxes.Option[];
  value?: string;
  onChange: (clickedValue: string) => void;
  collapseLevel?: number;
  nestedOption: Checkboxes.Option;
  optionsState: Checkboxes.OptionsState;
  optionState?: Checkboxes.CheckboxState;
  isInitiallyExpanded?: boolean;
}

export const CheckboxRow = ({
  options,
  optionState,
  onChange,
  isInitiallyExpanded = false,
  nestedOption,
  optionsState,
  collapseLevel,
}: Props) => {
  const [isExpanded, setIsExpanded] = React.useState(isInitiallyExpanded);
  const isParent = options.some(option => option.parentValue === nestedOption.value);

  const { sectionProps, triggerProps, sectionClassName } = useExpander({ isExpanded });

  const toggleSetIsCollapsed = () => {
    setIsExpanded(!isExpanded);
  };

  const canCollapse = isDefined(collapseLevel) && collapseLevel > 0;
  const shouldShowButton = canCollapse && isParent;

  return (
    <li key={nestedOption.value}>
      <div className="mb-2 flex items-center gap-2">
        <Checkbox
          onChange={event => onChange(event.target.value)}
          value={nestedOption.value}
          isChecked={optionState === 'checked'}
          isIndeterminate={optionState === 'indeterminate'}
        >
          <TextEllipsis noWrapText={false}>{nestedOption.name}</TextEllipsis>
        </Checkbox>
        {shouldShowButton && (
          <Button {...triggerProps} color="secondary" variant="text" isChevronOpen={isExpanded} onClick={toggleSetIsCollapsed} />
        )}
      </div>
      <section {...(canCollapse && sectionProps)} className={cx(sectionClassName, '-mt-2 overflow-hidden pt-2')}>
        <CheckboxGroup
          options={options}
          value={nestedOption.value}
          onChange={onChange}
          optionsState={optionsState}
          collapseLevel={isDefined(collapseLevel) ? collapseLevel - 1 : undefined}
        />
      </section>
    </li>
  );
};
